import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApiError } from 'shared/types';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import {
   JobManagementItem,
   JobManagementResponse,
   JobsBrowserActionResponse,
   JobsBrowserListItem,
   JobsBrowserListResponse,
   JobsBrowserPaginationMeta,
   JobsBrowserSort,
   JobsBrowserStatus,
} from './jobs-browser.types';

type StoreCallbacks = {
   onError?: (message: string) => void;
   onAuthError?: () => void;
};

const MANAGERS_PER_PAGE = 10;
const VACANCIES_PER_PAGE = 10;

export class JobsBrowserManagerVacanciesStore {
   readonly loadVacanciesState = new AsyncState<JobsBrowserListResponse>();
   readonly loadVacancyCountsState = new AsyncState<JobsBrowserListResponse>();
   readonly activateJobState = new AsyncState<JobsBrowserActionResponse>();
   readonly deactivateJobState = new AsyncState<JobsBrowserActionResponse>();

   manager: JobManagementItem;
   expanded = false;
   vacancies: JobsBrowserListItem[] = [];
   pagination: JobsBrowserPaginationMeta | null = null;
   sort: JobsBrowserSort = 'date';
   status: JobsBrowserStatus = JobsBrowserStatus.ACTIVE;
   filteredCountsSearchQuery: string | null = null;
   readonly filteredVacancyCounts = observable.map<JobsBrowserStatus, number>();

   constructor(manager: JobManagementItem) {
      this.manager = manager;

      makeObservable(this, {
         manager: observable,
         expanded: observable,
         vacancies: observable,
         pagination: observable,
         sort: observable,
         status: observable,
         filteredCountsSearchQuery: observable,
         loading: computed,
         actionLoading: computed,
         hasVacancies: computed,
         activeVacanciesCount: computed,
         archivedVacanciesCount: computed,
         totalVacanciesCount: computed,
         canPrev: computed,
         canNext: computed,
         recordStart: computed,
         recordEnd: computed,
         totalCount: computed,
         currentPage: computed,
         updateManager: action.bound,
         setExpanded: action.bound,
         toggleExpanded: action.bound,
         setSort: action.bound,
         setStatus: action.bound,
         onPrev: action.bound,
         onNext: action.bound,
      });
   }

   get loading(): boolean {
      return this.loadVacanciesState.status === 'pending';
   }

   get actionLoading(): boolean {
      return (
         this.activateJobState.status === 'pending' || this.deactivateJobState.status === 'pending'
      );
   }

   get hasVacancies(): boolean {
      return this.totalVacanciesCount > 0;
   }

   get activeVacanciesCount(): number {
      return this.filteredVacancyCounts.get(JobsBrowserStatus.ACTIVE) ?? this.manager.jobs_stats.active;
   }

   get archivedVacanciesCount(): number {
      return (
         this.filteredVacancyCounts.get(JobsBrowserStatus.ARCHIVED) ?? this.manager.jobs_stats.archived
      );
   }

   get totalVacanciesCount(): number {
      return this.activeVacanciesCount + this.archivedVacanciesCount;
   }

   get currentPage(): number {
      return this.pagination?.current_page ?? 1;
   }

   get totalCount(): number {
      return this.pagination?.total ?? 0;
   }

   get recordStart(): number {
      return this.pagination?.from ?? 0;
   }

   get recordEnd(): number {
      return this.pagination?.to ?? 0;
   }

   get canPrev(): boolean {
      return !this.loading && this.currentPage > 1;
   }

   get canNext(): boolean {
      return !this.loading && !!this.pagination && this.pagination.to < this.pagination.total;
   }

   updateManager(manager: JobManagementItem): void {
      this.manager = manager;
   }

   setExpanded(expanded: boolean): void {
      this.expanded = expanded;
   }

   async toggleExpanded(searchQuery: string, callbacks: StoreCallbacks = {}): Promise<void> {
      if (this.expanded) {
         this.setExpanded(false);
         return;
      }

      this.setExpanded(true);

      if (!this.pagination) {
         await this.loadVacancies({ searchQuery, page: 1, ...callbacks });
      }
   }

   async setSort(sort: JobsBrowserSort, searchQuery: string, callbacks: StoreCallbacks = {}): Promise<void> {
      this.sort = sort;
      await this.loadVacancies({ searchQuery, page: 1, sort, ...callbacks });
   }

   async setStatus(
      status: JobsBrowserStatus,
      searchQuery: string,
      callbacks: StoreCallbacks = {}
   ): Promise<void> {
      if (this.status === status && this.pagination) {
         return;
      }

      this.status = status;
      await this.loadVacancies({ searchQuery, page: 1, status, ...callbacks });
   }

   async onPrev(searchQuery: string, callbacks: StoreCallbacks = {}): Promise<void> {
      if (!this.canPrev) {
         return;
      }

      await this.loadVacancies({
         searchQuery,
         page: this.currentPage - 1,
         ...callbacks,
      });
   }

   async onNext(searchQuery: string, callbacks: StoreCallbacks = {}): Promise<void> {
      if (!this.canNext) {
         return;
      }

      await this.loadVacancies({
         searchQuery,
         page: this.currentPage + 1,
         ...callbacks,
      });
   }

   async loadVacancies({
      searchQuery,
      page = this.currentPage,
      sort = this.sort,
      status = this.status,
      onError,
      onAuthError,
   }: StoreCallbacks & {
      searchQuery: string;
      page?: number;
      sort?: JobsBrowserSort;
      status?: JobsBrowserStatus;
   }): Promise<void> {
      await this.loadVacanciesState.run(
         () =>
            HttpClient.get<JobsBrowserListResponse>(`/job/management/${this.manager.id}`, {
               params: {
                  page,
                  per_page: VACANCIES_PER_PAGE,
                  sort,
                  status,
                  search: searchQuery || null,
               },
            }),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.vacancies = resp.data ?? [];
                     this.pagination = resp.meta;
                     this.sort = sort;
                     this.status = status;
                  });

                  this.syncFilteredVacanciesCount({
                     searchQuery,
                     status,
                     total: resp.meta.total,
                  });

                  await this.loadOppositeFilteredVacanciesCount({
                     searchQuery,
                     status,
                     sort,
                     onError,
                     onAuthError,
                  });
                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message);
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   private syncFilteredVacanciesCount({
      searchQuery,
      status,
      total,
   }: {
      searchQuery: string;
      status: JobsBrowserStatus;
      total: number;
   }): void {
      const normalizedSearchQuery = searchQuery.trim();

      runInAction(() => {
         if (!normalizedSearchQuery) {
            this.filteredCountsSearchQuery = null;
            this.filteredVacancyCounts.clear();
            return;
         }

         if (this.filteredCountsSearchQuery !== normalizedSearchQuery) {
            this.filteredCountsSearchQuery = normalizedSearchQuery;
            this.filteredVacancyCounts.clear();
         }

         this.filteredVacancyCounts.set(status, total);
      });
   }

   private async loadOppositeFilteredVacanciesCount({
      searchQuery,
      status,
      sort,
      onError,
      onAuthError,
   }: StoreCallbacks & {
      searchQuery: string;
      status: JobsBrowserStatus;
      sort: JobsBrowserSort;
   }): Promise<void> {
      const normalizedSearchQuery = searchQuery.trim();

      if (!normalizedSearchQuery) {
         return;
      }

      const oppositeStatus =
         status === JobsBrowserStatus.ACTIVE
            ? JobsBrowserStatus.ARCHIVED
            : JobsBrowserStatus.ACTIVE;

      await this.loadVacancyCountsState.run(
         () =>
            HttpClient.get<JobsBrowserListResponse>(`/job/management/${this.manager.id}`, {
               params: {
                  page: 1,
                  per_page: 1,
                  sort,
                  status: oppositeStatus,
                  search: normalizedSearchQuery,
               },
            }),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  this.syncFilteredVacanciesCount({
                     searchQuery: normalizedSearchQuery,
                     status: oppositeStatus,
                     total: resp.meta.total,
                  });
                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message);
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   async activateJob({
      slug,
      searchQuery,
      onSuccess,
      onError,
      onAuthError,
   }: StoreCallbacks & {
      slug: string;
      searchQuery: string;
      onSuccess?: (message: string) => void;
   }): Promise<void> {
      await this.activateJobState.run(
         () => HttpClient.post<JobsBrowserActionResponse>(`/job/activate/${slug}`, null),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  this.updateManagerJobStats('activate');
                  onSuccess?.(resp.message);
                  await this.loadVacancies({ searchQuery, onError, onAuthError });
                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message);
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   async deactivateJob({
      slug,
      searchQuery,
      onSuccess,
      onError,
      onAuthError,
   }: StoreCallbacks & {
      slug: string;
      searchQuery: string;
      onSuccess?: (message: string) => void;
   }): Promise<void> {
      await this.deactivateJobState.run(
         () => HttpClient.post<JobsBrowserActionResponse>(`/job/deactivate/${slug}`, null),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  this.updateManagerJobStats('deactivate');
                  onSuccess?.(resp.message);
                  await this.loadVacancies({ searchQuery, onError, onAuthError });
                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message);
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   private updateManagerJobStats(actionType: 'activate' | 'deactivate'): void {
      const activeDelta = actionType === 'activate' ? 1 : -1;
      const archivedDelta = actionType === 'activate' ? -1 : 1;
      const active = Math.max(0, this.manager.jobs_stats.active + activeDelta);
      const archived = Math.max(0, this.manager.jobs_stats.archived + archivedDelta);

      runInAction(() => {
         this.manager = {
            ...this.manager,
            active_jobs:
               this.manager.active_jobs === undefined
                  ? undefined
                  : Math.max(0, this.manager.active_jobs + activeDelta),
            archived_jobs:
               this.manager.archived_jobs === undefined
                  ? undefined
                  : Math.max(0, this.manager.archived_jobs + archivedDelta),
            jobs_stats: {
               active,
               archived,
            },
         };
      });
   }

   private isAuthMessage(message?: string): boolean {
      return !!message && /token has expired/i.test(message);
   }

   private shouldTreatAsAuthError(error: ApiError): boolean {
      return this.isAuthMessage(error.message) || error.message === 'No response from server';
   }
}

export class FeatureJobsBrowserStore {
   readonly loadManagersState = new AsyncState<JobManagementResponse>();

   managers: JobManagementItem[] = [];
   pagination: JobsBrowserPaginationMeta | null = null;
   searchInput = '';
   searchQuery = '';
   readonly vacancyStores = observable.map<number, JobsBrowserManagerVacanciesStore>();

   constructor() {
      makeObservable(this, {
         managers: observable,
         pagination: observable,
         searchInput: observable,
         searchQuery: observable,
         loading: computed,
         currentPage: computed,
         recordStart: computed,
         recordEnd: computed,
         totalCount: computed,
         canPrev: computed,
         canNext: computed,
         setSearchInput: action.bound,
         submitSearch: action.bound,
         onPrev: action.bound,
         onNext: action.bound,
         getVacancyStore: action.bound,
      });
   }

   get loading(): boolean {
      return this.loadManagersState.status === 'pending';
   }

   get currentPage(): number {
      return this.pagination?.current_page ?? 1;
   }

   get recordStart(): number {
      return this.pagination?.from ?? 0;
   }

   get recordEnd(): number {
      return this.pagination?.to ?? 0;
   }

   get totalCount(): number {
      return this.pagination?.total ?? 0;
   }

   get canPrev(): boolean {
      return !this.loading && this.currentPage > 1;
   }

   get canNext(): boolean {
      return !this.loading && !!this.pagination && this.pagination.to < this.pagination.total;
   }

   setSearchInput(value: string): void {
      this.searchInput = value;
   }

   async submitSearch(callbacks: StoreCallbacks = {}): Promise<void> {
      if (this.searchQuery === this.searchInput && this.pagination) {
         return;
      }

      this.searchQuery = this.searchInput;
      await this.loadManagers({ page: 1, resetVacancies: true, ...callbacks });
   }

   async onPrev(callbacks: StoreCallbacks = {}): Promise<void> {
      if (!this.canPrev) {
         return;
      }

      await this.loadManagers({ page: this.currentPage - 1, resetVacancies: true, ...callbacks });
   }

   async onNext(callbacks: StoreCallbacks = {}): Promise<void> {
      if (!this.canNext) {
         return;
      }

      await this.loadManagers({ page: this.currentPage + 1, resetVacancies: true, ...callbacks });
   }

   getVacancyStore(manager: JobManagementItem): JobsBrowserManagerVacanciesStore {
      const existingStore = this.vacancyStores.get(manager.id);

      if (existingStore) {
         existingStore.updateManager(manager);
         return existingStore;
      }

      const store = new JobsBrowserManagerVacanciesStore(manager);
      this.vacancyStores.set(manager.id, store);

      return store;
   }

   async loadManagers({
      page = this.currentPage,
      resetVacancies = false,
      onError,
      onAuthError,
   }: StoreCallbacks & {
      page?: number;
      resetVacancies?: boolean;
   } = {}): Promise<void> {
      await this.loadManagersState.run(
         () =>
            HttpClient.get<JobManagementResponse>('/job/management', {
               params: {
                  page,
                  perPage: MANAGERS_PER_PAGE,
                  search: this.searchQuery || null,
               },
            }),
         {
            onSuccess: async (resp) => {
               if (resp.status === 200) {
                  const managers = resp.data ?? [];
                  const firstManager = managers[0] ?? null;
                  const shouldLoadFirstManager = !!firstManager && firstManager.jobs_count > 0;

                  runInAction(() => {
                     this.managers = managers;
                     this.pagination = resp.meta;

                     if (resetVacancies) {
                        this.vacancyStores.clear();
                     }

                     this.managers.forEach((manager, index) => {
                        const vacancyStore = this.getVacancyStore(manager);

                        if (index === 0 && manager.jobs_count > 0) {
                           vacancyStore.setExpanded(true);
                        }
                     });
                  });

                  if (shouldLoadFirstManager) {
                     const firstExpandedStore = this.getVacancyStore(firstManager);

                     await firstExpandedStore.loadVacancies({
                        searchQuery: this.searchQuery,
                        page: 1,
                        onError,
                        onAuthError,
                     });
                  }

                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message);
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   private isAuthMessage(message?: string): boolean {
      return !!message && /token has expired/i.test(message);
   }

   private shouldTreatAsAuthError(error: ApiError): boolean {
      return this.isAuthMessage(error.message) || error.message === 'No response from server';
   }
}
